import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SearchBar } from '../components/SearchBar';
import { NoResultsFound } from '../components/NoResultsFound';
import { LocationList } from '../components/LocationList';
import { Map } from '../components/Map';
import { PopularLocations } from '../components/PopularLocations';
import { PizzaLocation } from '../types';
import { searchPizzaPlaces } from '../services/locationService';
import { MapPin, Star, Clock, Shield, Utensils, ChefHat, Heart, Leaf } from 'lucide-react';

const chineseFoodFacts = [
  "Did you know? Chinese cuisine has 8 major regional traditions, each with unique flavors!",
  "Fun fact: Chopsticks were invented in China over 3,000 years ago!",
  "Food fact: Tea was discovered in China over 5,000 years ago!",
  "Did you know? The wok was invented during the Han Dynasty over 2,000 years ago!",
  "Cooking fact: Chinese cuisine follows the philosophy of balancing five flavors!",
  "Food fact: The world's oldest noodles were discovered in China!",
  "Cooking fact: Dim sum tradition dates back to the ancient Tea Road!",
  "Did you know? Peking Duck was originally created for Chinese emperors!"
];

export const Home: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState<PizzaLocation[]>([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral>({ lat: 40.7128, lng: -74.0060 });
  const [currentFact, setCurrentFact] = useState(chineseFoodFacts[0]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentFact(prevFact => {
        const currentIndex = chineseFoodFacts.indexOf(prevFact);
        const nextIndex = (currentIndex + 1) % chineseFoodFacts.length;
        return chineseFoodFacts[nextIndex];
      });
    }, 6000);

    return () => clearInterval(intervalId);
  }, []);

  const geocodeAddress = async (address: string): Promise<google.maps.LatLngLiteral> => {
    if (!window.google?.maps) {
      throw new Error('Google Maps not loaded. Please try again.');
    }

    const geocoder = new google.maps.Geocoder();
    
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else if (status === 'ZERO_RESULTS') {
          reject(new Error('Address not found. Please try a different location.'));
        } else {
          reject(new Error('Failed to find location. Please try again.'));
        }
      });
    });
  };

  const handleSearch = async (address: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const coordinates = await geocodeAddress(address);
      setUserLocation(coordinates);
      const pizzaPlaces = await searchPizzaPlaces(coordinates);
      setLocations(pizzaPlaces);
      setSearchPerformed(true);
    } catch (err: any) {
      setError(err.message || 'Failed to search for locations. Please try again.');
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Chinese Food Maps - Find Local Chinese Food Near Me</title>
        <meta name="description" content="Find delicious Chinese food near you with Chinese Food Maps! Our app shows you every Chinese restaurant within 10 miles of your location, complete with ratings, directions, and contact info." />
        <meta property="og:title" content="Chinese Food Maps - Find Local Chinese Food Near Me" />
        <meta property="og:description" content="Find delicious Chinese food near you with Chinese Food Maps! Our app shows you every Chinese restaurant within 10 miles of your location." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chinesefoodmaps.com" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com" />
      </Helmet>
      <div className="flex flex-col min-h-[calc(100vh-64px)]">
        <div className="bg-black">
          <div className="container mx-auto">
            <div className="px-4 py-4">
              <SearchBar 
                onSearch={handleSearch}
                isLoading={isLoading}
              />
              {error && (
                <p className="text-center text-sm text-gray-200 mt-2">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="w-full">
          <Map 
            locations={locations} 
            center={userLocation} 
            searchPerformed={searchPerformed}
          />
        </div>

        <div className="container mx-auto px-4 py-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-700"></div>
            </div>
          )}

          {locations.length > 0 && (
            <LocationList locations={locations} userLocation={userLocation} />
          )}

          {searchPerformed && locations.length === 0 && !isLoading && !error && (
            <NoResultsFound />
          )}

          <div className="space-y-8 mt-8">
            <PopularLocations />

            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center gap-2">
                <img 
                  src="https://cdn-icons-png.flaticon.com/512/661/661740.png"
                  alt="Chinese Food icon"
                  className="w-7 h-7"
                />
                Welcome to Chinese Food Maps
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Chinese Food Maps is your ultimate guide to authentic Chinese cuisine in your area. We help you discover every Chinese restaurant within a 10-mile radius, from traditional dim sum houses to modern fusion establishments. Whether you're craving spicy Sichuan dishes, delicate Cantonese cuisine, or hearty Northern Chinese specialties, we'll help you find the perfect spot.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-16">
                Our platform goes beyond just finding restaurants - we're your companion in exploring the rich tapestry of Chinese culinary traditions. Each restaurant listing includes detailed information about regional specialties, signature dishes, and authentic cooking methods. We help you understand the unique characteristics of different regional cuisines, from the fiery flavors of Sichuan to the subtle elegance of Cantonese cooking.
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-700 flex items-center gap-3">
                    <MapPin className="text-red-600 w-8 h-8" strokeWidth={2.5} />
                    How Chinese Food Maps Works
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">1.</span>
                      Share your location or search by area
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">2.</span>
                      Discover authentic Chinese restaurants nearby
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">3.</span>
                      Browse menus, specialties, and reviews
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">4.</span>
                      Connect directly with restaurants
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-700 flex items-center gap-3">
                    <Heart className="text-red-600 w-8 h-8" strokeWidth={2.5} />
                    Why Chinese Food Lovers Choose Us
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center gap-3">
                      <Leaf className="text-red-600 w-6 h-6" />
                      Find restaurants by regional cuisine
                    </li>
                    <li className="flex items-center gap-3">
                      <Shield className="text-red-600 w-6 h-6" />
                      Verified authentic Chinese restaurants
                    </li>
                    <li className="flex items-center gap-3">
                      <Clock className="text-red-600 w-6 h-6" />
                      Current opening hours and special offers
                    </li>
                    <li className="flex items-center gap-3">
                      <Star className="text-red-600 w-6 h-6" />
                      Reviews from Chinese food enthusiasts
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <ChefHat className="w-7 h-7 text-red-600" />
                How to Make Kung Pao Chicken
              </h2>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <h3 className="text-xl font-semibold text-red-800 mb-4">Prepare the Ingredients</h3>
                    <p className="text-gray-600 text-lg mb-4">
                      Start with 1 lb chicken thighs, cut into bite-sized pieces. Mix with 1 tbsp soy sauce, 1 tbsp Shaoxing wine, and 1 tsp cornstarch. Let marinate for 30 minutes. Prepare dried red chilies, peanuts, and Sichuan peppercorns.
                    </p>
                  </div>
                  <div className="h-[250px] overflow-hidden rounded-lg shadow-md">
                    <img 
                      src="https://images.pexels.com/photos/2664216/pexels-photo-2664216.jpeg"
                      alt="Kung Pao Chicken ingredients"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="h-[250px] overflow-hidden rounded-lg shadow-md order-2 md:order-1">
                    <img 
                      src="https://images.pexels.com/photos/5836771/pexels-photo-5836771.jpeg"
                      alt="Stir-frying process"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="order-1 md:order-2">
                    <h3 className="text-xl font-semibold text-red-800 mb-4">The Cooking Process</h3>
                    <p className="text-gray-600 text-lg">
                      Heat your wok until smoking. Stir-fry dried chilies and Sichuan peppercorns until fragrant. Add marinated chicken and stir-fry until golden. Add sauce mixture (soy sauce, vinegar, sugar) and continue cooking until the sauce thickens.
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <h3 className="text-xl font-semibold text-red-800 mb-4">Finishing Touches</h3>
                    <p className="text-gray-600 text-lg mb-4">
                      Add roasted peanuts and green onions. Toss everything together until well combined. The sauce should be glossy and coating all ingredients evenly.
                    </p>
                    <p className="text-gray-600 text-lg">
                      Serve hot with steamed rice. The dish should have the perfect balance of spicy, sweet, and savory flavors with the unique numbing sensation from the Sichuan peppercorns.
                    </p>
                  </div>
                  <div className="h-[250px] overflow-hidden rounded-lg shadow-md">
                    <img 
                      src="https://images.pexels.com/photos/6941010/pexels-photo-6941010.jpeg"
                      alt="Final Kung Pao Chicken dish"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <img 
                  src="https://cdn-icons-png.flaticon.com/512/661/661740.png"
                  alt="Chinese Food icon"
                  className="w-7 h-7"
                />
                Why I Started Chinese Food Maps
              </h2>
              <div className="prose max-w-none text-gray-600">
                <p className="text-lg leading-relaxed mb-4">
                  Growing up in a Chinese household, I was immersed in the rich tapestry of Chinese cuisine from an early age. The sizzle of the wok, the aromatic blend of spices, and the importance of balance in every dish became second nature to me.
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  The idea for Chinese Food Maps came from my own experiences trying to find authentic Chinese restaurants in new cities. I'd keep notes about different places - "best dim sum in town," "authentic Sichuan spice," "traditional Northern Chinese dumplings." Friends would constantly ask for recommendations, and I realized there was a need for a better way to discover genuine Chinese cuisine.
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  What started as a personal collection of favorite spots has grown into a comprehensive guide to Chinese cuisine across the country. We're not just about finding any Chinese restaurant - we're about helping you discover specific regional cuisines and dishes you're craving, whether it's hand-pulled noodles from Xi'an or delicate Cantonese seafood.
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  Our mission is to make it easier for everyone to explore the incredible diversity of Chinese cuisine. Whether you're a longtime fan of Chinese food or trying it for the first time, we're here to guide you to your next memorable meal.
                </p>
                <p className="text-lg leading-relaxed">
                  Join us in exploring the wonderful world of Chinese cuisine, one restaurant at a time. 干杯 (Gānbēi)! 🥢
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Utensils className="w-7 h-7 text-red-600" />
                Fun Chinese Food Facts
              </h2>
              <div className="animate-fadeIn">
                <p className="text-gray-700 text-lg">{currentFact}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;